import React, { useEffect, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ClockTypes } from "@repo/database";
import { format } from "date-fns";
import { useShallow } from "zustand/react/shallow";

import { useEmployee } from "~/hooks/use-employee";
import { useUser } from "~/hooks/use-user";
import { cn } from "~/lib/utils";
import { useCartStore } from "~/stores/use-cart-store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import CartItemContext from "./cart-item-context";

export default function CartItemContextDialog() {
  const { employees } = useEmployee();
  const { user } = useUser();

  const { cartItemContextOpen, setCartItemContextOpen, selectedItem, addItem } =
    useCartStore(
      useShallow((state) => ({
        selectedItem: state.selectedItem,
        cartItemContextOpen: state.cartItemContextOpen,
        setCartItemContextOpen: state.setCartItemContextOpen,
        addItem: state.addItem,
      })),
    );

  const [itemName, setItemName] = useState(selectedItem?.name ?? "");

  const handleDialog = (open: boolean) => {
    if (open === false) {
      setCartItemContextOpen(false);
    } else {
      setCartItemContextOpen(true);
    }
  };

  useEffect(() => {
    if (!selectedItem) return;
    setItemName(selectedItem.name);
  }, [selectedItem]);

  return (
    <Dialog open={cartItemContextOpen} onOpenChange={handleDialog}>
      <DialogContent
        className="max-w-lg w-full"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle></DialogTitle>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
          <div className="flex flex-col gap-1">
            <Textarea
              readOnly={selectedItem?.ordered === true}
              className={cn(
                "p-0 text-xl font-medium !min-h-8 max-h-8 resize-none rounded-none border-0 hide-scrollbar",
                selectedItem?.ordered && "cursor-not-allowed !ring-0 ",
              )}
              value={itemName}
              onChange={(e) => {
                setItemName(e.target.value);
                if (!selectedItem) return;
                addItem({
                  ...selectedItem,
                  name: e.target.value,
                });
              }}
            />
            {selectedItem && (
              <div className="flex flex-row gap-2 items-center">
                <span className="text-sm text-muted-foreground">
                  {
                    employees.find(
                      (employee) => employee.id === selectedItem.employeeId,
                    )?.firstName
                  }
                </span>
                <span className="text-sm text-muted-foreground">-</span>
                <span className="text-sm text-muted-foreground">
                  {format(
                    selectedItem.createdAt,
                    user.clock === ClockTypes.military
                      ? "dd-MM-yyyy HH:mm:ss"
                      : "MM/dd/yyyy hh:aa:ss a",
                  )}
                </span>
              </div>
            )}
          </div>
        </DialogHeader>
        <ScrollArea className="max-h-[80vh] p-4">
          {selectedItem && (
            <CartItemContext
              item={selectedItem}
              onClose={() => handleDialog(false)}
            />
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
